import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';

import { useIsSelectedProviderInOnboarding } from '@provider-portal/common/hooks/useIsSelectedProviderInOnboarding';
import { VendorAssignedProvider } from '@provider-portal/common/services/types/account';
import { StoreListGroup } from '@provider-portal/mp2-common/components/StoreSelector/StoreSelectorStoresList';
import { getFilteredTabHeaderForRoute } from 'root/navigation/navStructureUtils';
import {
  selectAssignedProvidersByVendor,
  selectSelectedProvider,
  selectSelectedProviderPermissions,
} from 'rdx/account';

import { NavBarMenuItem } from './NavBarMenu';
import { getModuleNameFromMenuCategoryList } from './utils';
import { selectTargetingParameters } from 'rdx/targetingParameters/selectors';

interface ModuleInfo {
  moduleName: string | null;
  tabName: string | null;
  tabRank: number | null;
  module: ReturnType<typeof getFilteredTabHeaderForRoute>;
}

/**
 * Hook to get module information for a given route
 */
export function useGetModuleInfo(menuCategoryList: NavBarMenuItem[]) {
  const providerPermissions = useSelector(selectSelectedProviderPermissions, isEqual);
  const targetingParameters = useSelector(selectTargetingParameters);
  const isSelectedProviderInOnboarding = useIsSelectedProviderInOnboarding();

  return (route: string): ModuleInfo => {
    const module = getFilteredTabHeaderForRoute(
      route,
      providerPermissions,
      targetingParameters,
      isSelectedProviderInOnboarding,
    );

    if (!module) {
      return {
        moduleName: getModuleNameFromMenuCategoryList(menuCategoryList, route),
        tabName: null,
        tabRank: null,
        module: null,
      };
    }

    const tab = module.tabs.find((t) => t.route === route);
    const tabRank = tab ? module.tabs.indexOf(tab) + 1 : null;

    return {
      moduleName: module.id,
      tabName: tab?.id ?? null,
      tabRank,
      module,
    };
  };
}

/**
 * Hook to build venue selector configuration
 */
export function useVenueSelectorConfig(
  isSelectedProviderInOnboarding: boolean,
  onProviderSelect: (storeId: number) => void,
  onAddVenue: () => void,
) {
  const selectedProvider = useSelector(selectSelectedProvider);
  const assignedProvidersByVendor: Map<number, VendorAssignedProvider> | undefined = useSelector(
    selectAssignedProvidersByVendor,
  );

  return (() => {
    if (!selectedProvider || !assignedProvidersByVendor) {
      throw new Error('Venue selector requires a selected provider and assigned providers.');
    }

    const stores: StoreListGroup[] = [];

    for (const vendorProviders of assignedProvidersByVendor.values()) {
      if (vendorProviders.providers.length > 0) {
        stores.push({
          parentName: vendorProviders.vendor_name,
          stores: vendorProviders.providers.map((provider) => ({
            storeName: provider.provider_name,
            storeId: provider.provider_id,
          })),
        });
      }
    }

    const showAddVenueButton = !isSelectedProviderInOnboarding;

    return {
      parentName: selectedProvider.vendorName || '',
      storeName: selectedProvider.providerName || '',
      selectedStoreId: selectedProvider.providerId,
      stores,
      onProviderSelect,
      showAddVenueButton,
      onAddVenue,
    };
  })();
}
